import {
  type ChangeEvent,
  type ComponentType,
  type InputHTMLAttributes,
  useCallback,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import { type ControlStatus } from '@/components/inputs/control-status'
import {
  type ControlSize,
  useControlSize,
} from '@/components/inputs/control-size'
import DatePicker from 'react-date-picker'
import { Icon, type IconName } from '@/components/Icon'
import { NEVER, type Observable } from 'rxjs'
import { useFocusTracking, useObservableFocus } from '@/hooks/focus-hooks'
import DateTimePicker from 'react-datetime-picker'
import 'react-date-picker/dist/DatePicker.css'
import 'react-datetime-picker/dist/DateTimePicker.css'
import 'react-calendar/dist/Calendar.css'
import 'react-clock/dist/Clock.css'
import { match } from '@/utils/pattern-match-util'
import styles from './DateInput.module.css'

export interface DateInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'disabled' | 'size'
> {
  isDisabled?: boolean
  status?: ControlStatus
  /**
   * Defaults to the size of the surrounding `ControlSizeContext`.
   */
  size?: ControlSize
  value?: Date | null
  includeTime?: boolean
  minDate?: Date
  maxDate?: Date
  onChange?: (value: Date, e: ChangeEvent<HTMLInputElement>) => unknown
  icon?: IconName
  compact?: boolean
  focus$?: Observable<unknown>
  format?: string
  clearIcon?: boolean
  calendarClassName?: string
  isNotValid?: boolean
  testId?: string
}

/**
 * A date input with a calendar picker, optionally with a time.
 */
export function DateInput({
  className,
  value,
  onChange,
  status = 'default',
  size: sizeProp,
  includeTime,
  testId,
  onFocus,
  onBlur,
  focus$ = NEVER,
  icon,
  format,
  compact,
  calendarClassName,
  isNotValid,
  isDisabled,
  ...props
}: DateInputProps) {
  const size = useControlSize(sizeProp)
  // The two pickers share these props at runtime, but their typings disagree
  // (`onChange` value, `ref`, clock props), so the element is typed loosely.
  const Element = (includeTime
    ? DateTimePicker
    : DatePicker) as unknown as ComponentType<Record<string, unknown>>
  const rootRef = useRef<HTMLDivElement>(null)
  const [focusProps, focused] = useFocusTracking({ onFocus, onBlur })
  const [isCalendarOpen, setCalendarOpen] = useState<true | null>(null)
  const openCalendar = useCallback(() => setCalendarOpen(true), [])
  const unmountCalendar = useCallback(() => setCalendarOpen(null), [])

  useObservableFocus(
    focus$,
    useCallback(() => {
      // The pickers expose no focus method; `data-input` marks the visible
      // segment inputs, skipping the hidden native input before them.
      const input =
        rootRef.current?.querySelector<HTMLInputElement>('input[data-input]')
      if (!input) {
        console.warn(
          'Could not find input to focus for Date component. Was there a breaking change?',
        )
      } else {
        input.focus()
      }
    }, []),
  )
  return (
    <div
      className={cx(
        styles.input,
        match(size, {
          normal: undefined,
          compact: styles.compact,
          small: styles.small,
        }),
        isDisabled && styles.disabled,
        focused && styles.focused,
        isNotValid && styles.isNotValid,
        styles[status],
        className,
      )}
      data-test={testId}
      ref={rootRef}
    >
      <Element
        className={cx(!compact && styles.dense)}
        onChange={onChange}
        calendarProps={{ className: calendarClassName }}
        {...(includeTime ? { isCalendarOpen } : { isOpen: isCalendarOpen })}
        onCalendarOpen={openCalendar}
        onCalendarClose={unmountCalendar}
        value={value}
        disableClock
        clearIcon={null}
        calendarAriaLabel="Open calendar"
        format={format ?? `yyyy/MM/dd${includeTime ? ' h:mm a' : ''}`}
        yearPlaceholder="yyyy"
        monthPlaceholder="mm"
        dayPlaceholder="dd"
        hourPlaceholder="hh"
        minutePlaceholder="mm"
        secondPlaceholder="ss"
        calendarIcon={
          // Not a button: the date picker already wraps it in one. Matches
          // the toggle color of `CustomSelect`.
          <span
            className={styles.calendarToggle}
            data-test={`${testId}-calendar-toggle`}
          >
            <Icon
              name={icon ?? 'arrow'}
              color={icon ? undefined : '#8993a4'}
              rotate={icon ? 0 : 90}
            />
          </span>
        }
        disabled={isDisabled}
        {...props}
        {...focusProps}
      />
    </div>
  )
}
